import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { FolderPlus, GripVertical, Trash2, X, Pencil } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default categories that cannot be deleted
const DEFAULT_CATEGORIES = ["scaffolding", "safety_gear", "clothing", "tools", "accessories", "nets_tarps"];

export const CategoryFilter = ({ 
  categories, 
  setCategories,
  selectedCategory, 
  setSelectedCategory, 
  products,
  onAddCategory
}) => {
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [categoryToEdit, setCategoryToEdit] = useState(null);
  const [draggedCategory, setDraggedCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({ id: "", name: "", icon: "📦" });
  const [editForm, setEditForm] = useState({ name: "", icon: "" });

  const handleCategoryDragStart = (e, category) => {
    setDraggedCategory(category);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleCategoryDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleCategoryDrop = (e, targetCategory) => {
    e.preventDefault();
    if (!draggedCategory || draggedCategory.id === targetCategory.id) return;
    
    const newCategories = [...categories];
    const dragIndex = newCategories.findIndex(c => c.id === draggedCategory.id);
    const dropIndex = newCategories.findIndex(c => c.id === targetCategory.id);
    
    newCategories.splice(dragIndex, 1);
    newCategories.splice(dropIndex, 0, draggedCategory);
    
    setCategories(newCategories);
    setDraggedCategory(null);
    toast.success("Category order updated!");
    
    localStorage.setItem('webshop_categories', JSON.stringify(newCategories));
  };

  const addCategory = async () => {
    if (!newCategory.id || !newCategory.name) {
      toast.error("Category ID and name are required");
      return;
    }

    if (onAddCategory) {
      await onAddCategory(newCategory);
    } else {
      setCategories(prev => [...prev, newCategory]);
      toast.success("Category added!");
    }
    
    setNewCategory({ id: "", name: "", icon: "📦" });
    setCategoryDialogOpen(false);
  };

  const openEditDialog = (cat, e) => {
    e.stopPropagation();
    setCategoryToEdit(cat);
    setEditForm({ name: cat.name, icon: cat.icon });
    setEditDialogOpen(true);
  };

  const handleEditCategory = async () => {
    if (!categoryToEdit || !editForm.name) {
      toast.error("Category name is required");
      return;
    }

    try {
      await axios.put(`${API}/shop/categories/${categoryToEdit.id}`, {
        name: editForm.name,
        icon: editForm.icon
      });
      
      // Update local state
      setCategories(prev => prev.map(c => 
        c.id === categoryToEdit.id 
          ? { ...c, name: editForm.name, icon: editForm.icon }
          : c
      ));
      
      toast.success("Category updated!");
      setEditDialogOpen(false);
      setCategoryToEdit(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update category");
    }
  };

  const handleDeleteCategory = async () => {
    if (!categoryToDelete) return;
    
    try {
      await axios.delete(`${API}/shop/categories/${categoryToDelete.id}`);
      setCategories(prev => prev.filter(c => c.id !== categoryToDelete.id));
      if (selectedCategory === categoryToDelete.id) {
        setSelectedCategory("");
      }
      toast.success("Category deleted!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete category");
    } finally {
      setDeleteConfirmOpen(false);
      setCategoryToDelete(null);
    }
  };

  const openDeleteConfirm = (cat, e) => {
    e.stopPropagation();
    setCategoryToDelete(cat);
    setDeleteConfirmOpen(true);
  };

  return (
    <div className="flex flex-wrap gap-2 items-center">
      {/* Add Category Button - FIRST */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="border-dashed border-[#23F7DD]/50 text-[#23F7DD] hover:border-[#23F7DD] hover:bg-[#23F7DD]/10">
            <FolderPlus className="w-4 h-4 mr-1" />
            Add Category
          </Button>
        </DialogTrigger>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-['Unbounded']">Add New Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-[#94A3B8] mb-1 block">Category Name</label>
              <Input
                value={newCategory.name}
                onChange={(e) => setNewCategory(prev => ({
                  ...prev,
                  name: e.target.value,
                  id: e.target.value.toLowerCase().replace(/\s+/g, '_')
                }))}
                placeholder="e.g., Safety Equipment"
                className="bg-black/50 border-white/10 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-[#94A3B8] mb-1 block">Icon (Emoji)</label>
              <Input
                value={newCategory.icon}
                onChange={(e) => setNewCategory(prev => ({ ...prev, icon: e.target.value }))}
                placeholder="📦"
                className="bg-black/50 border-white/10 text-white text-2xl"
              />
            </div>
            <Button
              onClick={addCategory}
              className="w-full bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
            >
              Add Category
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-['Unbounded']">Edit Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-[#94A3B8] mb-1 block">Category Name</label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Safety Equipment"
                className="bg-black/50 border-white/10 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-[#94A3B8] mb-1 block">Icon (Emoji)</label>
              <Input
                value={editForm.icon}
                onChange={(e) => setEditForm(prev => ({ ...prev, icon: e.target.value }))}
                placeholder="📦"
                className="bg-black/50 border-white/10 text-white text-2xl"
              />
            </div>
            <Button
              onClick={handleEditCategory}
              className="w-full bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
            >
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Category Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-['Unbounded'] text-red-400">Delete Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[#94A3B8]">
              Are you sure you want to delete the category "<span className="text-white">{categoryToDelete?.name}</span>"?
            </p>
            <p className="text-xs text-red-400">
              Note: You can only delete custom categories that have no products.
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirmOpen(false)}
                className="flex-1 border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteCategory}
                className="flex-1 bg-red-500 text-white hover:bg-red-600"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Divider */}
      <div className="h-6 w-px bg-white/20"></div>
      
      {/* All Button */}
      <Button
        variant={selectedCategory === "" ? "default" : "outline"}
        onClick={() => setSelectedCategory("")}
        className={`${selectedCategory === "" ? "bg-[#23F7DD] text-black" : "border-white/20 text-white hover:border-[#23F7DD]"}`}
        size="sm"
      >
        All ({products.length})
      </Button>
      
      {/* Draggable Category Buttons */}
      {categories.map(cat => {
        const count = products.filter(p => p.category === cat.id).length;
        const isDefault = DEFAULT_CATEGORIES.includes(cat.id);
        return (
          <div key={cat.id} className="relative group flex items-center gap-1">
            <Button
              variant={selectedCategory === cat.id ? "default" : "outline"}
              onClick={() => setSelectedCategory(cat.id)}
              className={`${selectedCategory === cat.id ? "bg-[#23F7DD] text-black" : "border-white/20 text-white hover:border-[#23F7DD]"} cursor-grab active:cursor-grabbing`}
              size="sm"
              draggable
              onDragStart={(e) => handleCategoryDragStart(e, cat)}
              onDragOver={handleCategoryDragOver}
              onDrop={(e) => handleCategoryDrop(e, cat)}
            >
              <GripVertical className="w-3 h-3 mr-1 opacity-50" />
              <span className="mr-1">{cat.icon}</span>
              {cat.name} ({count})
            </Button>
            {/* Action buttons - visible on hover */}
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={(e) => openEditDialog(cat, e)}
                className="w-6 h-6 bg-blue-500/80 text-white rounded flex items-center justify-center hover:bg-blue-600 transition-colors"
                title="Edit category"
              >
                <Pencil className="w-3 h-3" />
              </button>
              {/* Delete button - only for custom categories with 0 products */}
              {!isDefault && count === 0 && (
                <button
                  onClick={(e) => openDeleteConfirm(cat, e)}
                  className="w-6 h-6 bg-red-500/80 text-white rounded flex items-center justify-center hover:bg-red-600 transition-colors"
                  title="Delete category"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
